import { ESignSigner } from './eSignSigner';

export type OneSpanEvent = {
  name: OneSpanEventName;
  packageId: string;
  documentId?: string | null;
  createdDate: string;
  message?: string | null;
  /**
   * The sessionUser field depends on whether or not the event was generated by a sender, or by a signer.
   * Sender-triggered events will have the sender ID. Signer-triggered events will have the signer ID
   */
  sessionUser?: string;
};

// You can find a list of all possible events here (under the 'Available Notifications' section):
// https://community.onespan.com/documentation/onespan-sign/guides/feature-guides/developer/setting-callback-notifications
export enum OneSpanEventName {
  // common events
  PACKAGE_CREATE = 'PACKAGE_CREATE',
  PACKAGE_ACTIVATE = 'PACKAGE_ACTIVATE',
  DOCUMENT_VIEWED = 'DOCUMENT_VIEWED',
  DOCUMENT_SIGNED = 'DOCUMENT_SIGNED',
  SIGNER_COMPLETE = 'SIGNER_COMPLETE',
  PACKAGE_COMPLETE = 'PACKAGE_COMPLETE',

  // uncommon / problematic events
  PACKAGE_DECLINE = 'PACKAGE_DECLINE',
  PACKAGE_OPT_OUT = 'PACKAGE_OPT_OUT',
  PACKAGE_EXPIRE = 'PACKAGE_EXPIRE',
  EMAIL_BOUNCE = 'EMAIL_BOUNCE',
  SIGNER_LOCKED = 'SIGNER_LOCKED',
}

type log = {
  timestamp: Date;
  error: any;
};

/**
 * OneSpan Events get stored in the db, and get additional db-related attributes
 */
export type StoredOneSpanEvent = OneSpanEvent & {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  status: OneSpanEventStatus;
  logs: log[];
  submissionRunId?: string;
};

export type OneSpanPackage = {
  id: string;
  eSignCeremonyId: string;
  senderId?: string;
  ceremonyEvents?: StoredOneSpanEvent[];
};

export type OneSpanSigner = {
  id: string;
  eSignSignerId: string;
  role: string;
};

export type OneSpanESignSigner = ESignSigner & {
  oneSpanId: string;
  oneSpanRole: string;
};

export enum OneSpanPackageErrorCode {
  ApplicationNotFoundError = 'OneSpanPackageApplicationNotFoundError',
  BuildGeneratedDocumentDataApplicationNameError = 'OneSpanBuildGeneratedDocumentDataApplicationNameError',
  SendESignDocumentNotFoundError = 'OneSpanPackageSendESignDocumentNotFoundError',
  ESignCeremonyNotFoundError = 'OneSpanPackageESignCeremonyNotFoundError',
  NotAllRequiredFilesUploaded = 'OneSpanPackageNotAllRequiredFilesUploaded',
  CreateError = 'OneSpanPackageCreateError',
  RetrieveStoredFileError = 'OneSpanPackageRetrieveStoredFileError',
  SendESignDocumentNotUploadedError = 'OneSpanPackageSendESignDocumentNotUploadedError',
  SendStoredFileNotFoundError = 'OneSpanPackageSendStoredFileNotFoundError',
  SendESignDocumentFileNotFoundError = 'OneSpanPackageSendESignDocumentFileNotFoundError',
}

export enum OneSpanPackageErrorMessage {
  ApplicationNotFoundError = 'Failed to send OneSpan package: application not found.',
  BuildGeneratedDocumentDataApplicationNameError = 'Could not retrieve application document name.',
  SendESignDocumentNotFoundError = 'Failed to send OneSpan package: no eSignDocuments found.',
  NotAllRequiredFilesUploaded = 'Failed to send OneSpan package: all required files are not uploaded.',
  ESignCeremonyNotFoundError = 'Could not send OneSpanPackage: ESignCeremony not found',
  CreateError = 'Failed to create OneSpan package.',
  RetrieveStoredFileError = 'Failed to send OneSpan package: could not retrieve PDF for stored file.',
  SendESignDocumentNotUploadedError = 'Failed to send OneSpan package: ESignDocument not uploaded.',
  SendStoredFileNotFoundError = 'Failed to send OneSpan package: Stored file not found.',
  SendESignDocumentFileNotFoundError = 'Failed to send OneSpan package: no stored file found for generated eSignDocument.',
}

// @deprecated Use OneSpanFieldSubtype from the @breathelife/onespan-integration instead.
export enum SignatureFieldType {
  signature = 'signature',
  initials = 'initials',
  capture = 'capture',
  mobile_capture = 'mobile_capture',
}

export enum OneSpanEventStatus {
  PROCESSING = 'PROCESSING',
  DONE = 'DONE',
  ERROR = 'ERROR',
}
